import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';

import {
  authenticate,
  getToken,
  SyncPair,
  updateSyncPair,
} from '~/api/evisync';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { CardsGrid } from '~/eds/src/components/Card/subcomponents/CardsGrid';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Section } from '~/eds/src/components/Section/Section';
import { CrmType } from '~/enums/CrmType';
import { EsignatureType } from '~/enums/EsignatureType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { ProcurementIntegrationType } from '~/enums/ProcurementIntegrationType';
import { QueryParamType } from '~/enums/QueryParamType';
import { StorageProviderType } from '~/enums/StorageProviderType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { ERROR } from '~/types/toast.types';
import { removeQueryParam } from '~/utils/browser';
import { createSyncPairData } from '~/utils/integrations';

import { getDomainKeys } from './filterOptions';
import { useGetAccessConditions } from './hooks';
import { IntegrationCard } from './IntegrationCard';
import { SyncPairWithAuth } from './types';
import { useSyncPairs } from './useSyncPairs';
import {
  cardMatchesFilters,
  cardMatchesSearch,
  checkFlagIncluded,
  getActionStatus,
  getChipStatusProps,
  isEsignatureIntegrationActive,
  resetSyncPairAuthCode,
  shouldDisableEsignatureConfigure,
} from './utils';

export const IntegrationCards = ({ search }: { search?: string }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { location, navigate, params } = useRouting();
  const { clientId } = params;

  const enableDocumentEditing = useFlag(FlagType.SharepointWizard);
  const enableWebhooksIntegration = useFlag(FlagType.Webhooks);
  const enableAribaIntegration = useFlag(FlagType.AribaControllerFlag);
  const hasApiIntegration = useHasFeatureFlag(FeatureFlagType.ExternalApi);

  const filters = useSelector(slices.newIntegrations.selectors.selectedFilters);
  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  ) as SyncPairWithAuth | undefined;

  const {
    syncPairs,
    endpointsData,
    isLoadingStorageSyncPairs,
    isLoadingAribaSyncPairs,
    isLoadingCrmConnections,
    isLoadingEsignatureIntegration,
    _isLoadingWebhooksIntegration,
    _activeEsignatureIntegrationCount,
    _eSignatureIntegrationErrorMessage,
    esignIntegrations,
  } = useSyncPairs(clientId);

  const hasConfiguredEndpoints = endpointsData.total > 0;
  const hasTokenId = Boolean(activeSyncPair?.tokenId);
  const isPairCreated = Boolean(activeSyncPair?.id);
  const isLoadingToken = Boolean(activeSyncPair?.state);

  const chipTranslationKeys = getDomainKeys(intl);
  const {
    comingSoon,
    futureDevelopment,
    configured,
    reconnect,
    configure,
    syncing,
    remove,
  } = useGetAccessConditions();

  const accessConditions = {
    configure,
    reconnect,
    configured,
    syncing,
    remove,
  };

  const authenticateErrorMessage = intl.formatMessage(
    {
      id: 'Authentication.errorMessage',
      defaultMessage: 'Unable to contact {name}, please try again.',
      description:
        'Error message when authentication fails. Example: name = "Google Drive"',
    },
    {
      name: activeSyncPair?.name,
    },
  );

  useEffect(() => {
    if (
      !hasTokenId &&
      activeSyncPair?.state &&
      activeSyncPair?.code &&
      activeSyncPair?.provider
    ) {
      dispatch(
        slices.integrations.actions.setActiveSyncPair(
          resetSyncPairAuthCode(activeSyncPair),
        ),
      );
      getToken({
        code: activeSyncPair.code,
        provider: activeSyncPair.provider,
        state: activeSyncPair.state,
      })
        .then((response) => {
          const updatedActiveSyncPair = {
            ...activeSyncPair,
            tokenId: response.id,
          };
          const cleanedSyncPair = resetSyncPairAuthCode(updatedActiveSyncPair);
          dispatch(
            slices.integrations.actions.setActiveSyncPair(cleanedSyncPair),
          );

          if (isPairCreated) {
            executeUpdate();
            dispatch(
              slices.integrations.actions.setManageSyncPairStage(
                ManageSyncPairStageType.None,
              ),
            );
          } else {
            handleCreateNext();
          }

          removeQueryParam(location, navigate, QueryParamType.States);
        })
        .catch(() => {
          dispatch(
            slices.integrations.actions.setManageSyncPairStage(
              ManageSyncPairStageType.Provider,
            ),
          );
          showToast(ERROR, authenticateErrorMessage);
        });
    }
  }, [activeSyncPair]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateNext = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.Folders,
      ),
    );
  };

  const handleStorageProviderAuth = (
    name: string,
    provider: StorageProviderType,
  ) => {
    const syncPairData = createSyncPairData({
      name,
      provider,
    });
    dispatch(slices.integrations.actions.setActiveSyncPair(syncPairData));
    executeAuthenticate();
  };

  const { executor: executeUpdate } = useAsync(
    updateSyncPair,
    activeSyncPair as SyncPair,
    {
      errorToastMessage: intl.formatMessage({
        id: 'Authentication.updateErrorMessage',
        defaultMessage: 'Unable to update sync pair, please try again.',
        description: 'Error message when updating sync pair fails',
      }),
      successToastMessage: intl.formatMessage(
        {
          id: 'Authentication.updateSuccessMessage',
          defaultMessage: 'Sync pair updated successfully name: {name}',
          description:
            'Success message when sync pair is updated. Example: name = "Google Drive"',
        },
        { name: activeSyncPair?.name },
      ),
      successHandler: (response) => {
        dispatch(slices.integrations.actions.setActiveSyncPair(response));
      },
    },
  );

  const { executor: executeAuthenticate } = useAsync(
    authenticate,
    {
      provider: activeSyncPair?.provider as StorageProviderType,
      returnTo: window.location.href,
    },
    {
      errorToastMessage: authenticateErrorMessage,
      successHandler: (response) => {
        window.location.href = response.url;
      },
    },
  );

  const baseManageAction = {
    level: 'subtle',
    text: intl.formatMessage({
      id: 'Integrations.manage',
      defaultMessage: 'Manage Integration',
      description: 'Manage integration action text',
    }),
  };

  const storageManageAction = {
    ...baseManageAction,
    onClick: () =>
      navigate(
        generatePath(RoutePathType.AdminConsoleClientDocumentManagement, {
          clientId: clientId as string,
        }),
      ),
  };

  const disableEsignatureConfigureTooltip = intl.formatMessage({
    id: 'Integrations.esignatureDisabledTooltip',
    defaultMessage:
      'Another e-signature integration is already active. Please remove it before configuring this one.',
    description:
      'Tooltip when e-signature integration is disabled because another is active',
  });

  //TODO: Implement this
  const hasConfiguredDocumentEditing = false;

  const sections = {
    WORKDAY: {
      label: intl.formatMessage({
        id: 'Integrations.workdaySection',
        defaultMessage: 'Workday',
        description: 'Workday integrations section label',
      }),
      name: 'workday',
      cards: [
        {
          chips: getChipStatusProps(['workday', 'clm'], chipTranslationKeys),
          actions: [comingSoon],
          description: intl.formatMessage({
            id: 'Integrations.clmForProcurementContractsDescription',
            defaultMessage:
              'This integration provides a system to streamline the creation, negotiation, and storage of procurement contracts.',
            description:
              'Description for the CLM for Procurement integration card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.contractLifecycleManagementCard',
            defaultMessage: 'Contract Lifecycle Management for Procurement',
            description:
              'Contract Lifecycle Management for Procurement integrations card label',
          }),
          name: 'contract-lifecycle-management',
          provider: 'workday',
        },
        {
          chips: getChipStatusProps(['workday', 'ci'], chipTranslationKeys),
          actions: [comingSoon],
          description: intl.formatMessage({
            id: 'Integrations.ciForWorkdayFinancialsDescription',
            defaultMessage:
              'This integration will use AI to transform unstructured contract data into valuable and actionable financial insights, to help finance teams manage revenue, costs, and compliance.',
            description:
              'Description for the CI for Workday Financials integration card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.contractIntelligenceForWorkdayFinancialsCard',
            defaultMessage: 'Contract Intelligence for Workday Financials',
            description:
              'Contract Intelligence for Workday Financials integrations card label',
          }),
          name: 'contract-intelligence-for-workday-financials',
          provider: 'workday',
        },
        {
          chips: getChipStatusProps(['workday', 'clm'], chipTranslationKeys),
          actions: [comingSoon],
          description: intl.formatMessage({
            id: 'Integrations.policyIngestionForEmployeeSelfServiceAgentDescription',
            defaultMessage:
              'This integration will allow an AI-powered agent to ingest company policy documents, providing employees with a self-service tool to get instant answers to their questions.',
            description:
              'Description for the Policy Ingestion for Employee Self-Service Agent card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.policyIngestionForEmployeeSelfServiceAgentCard',
            defaultMessage: 'Policy Ingestion for Employee Self-Service Agent',
            description:
              'Policy Ingestion for Employee Self-Service Agent integrations card label',
          }),
          name: 'policy-ingestion-for-employee-self-service-agent',
          provider: 'workday',
        },
        {
          chips: getChipStatusProps(['workday', 'ci'], chipTranslationKeys),
          actions: [comingSoon],
          description: intl.formatMessage({
            id: 'Integrations.contractIntelligenceForRevenueContractAgentDescription',
            defaultMessage:
              'This integration will use AI to analyze revenue-generating contracts, helping businesses prevent revenue leakage, track performance, and optimize commercial terms.',
            description:
              'Description for the Contract Intelligence for Revenue Contract Agent card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.contractIntelligenceForRevenueContractAgentCard',
            defaultMessage: 'Contract Intelligence for Revenue Contract Agent',
            description:
              'Contract Intelligence for Revenue Contract Agent integrations card label',
          }),
          name: 'contract-intelligence-for-revenue-contract-agent',
          provider: 'workday',
        },
        {
          chips: getChipStatusProps(
            ['workday', 'clm', 'ci'],
            chipTranslationKeys,
          ),
          actions: [comingSoon],
          description: intl.formatMessage({
            id: 'Integrations.workdaySSOForCLMAndCIDescription',
            defaultMessage:
              'This integration will enable SSO through Workday, allowing users to securely access both the Contract Lifecycle Management and Contract Intelligence platforms.',
            description: 'Description for the Workday SSO for CLM and CI cards',
          }),
          label: intl.formatMessage({
            id: 'Integrations.workdaySSOForCLMAndCI',
            defaultMessage: 'Workday SSO for CLM and CI',
            description: 'Workday SSO for CLM and CI integrations card label',
          }),
          name: 'workday-sso-for-clm-and-ci',
          provider: 'workday',
        },
        {
          chips: getChipStatusProps(['workday', 'clm'], chipTranslationKeys),
          actions: [futureDevelopment],
          description: intl.formatMessage({
            id: 'Integrations.contractLifecycleManagementForProfessionalServicesAutomationDescription',
            defaultMessage:
              'This integration will provide a comprehensive system for professional services firms to manage contracts, from creation and negotiation to execution and renewal.',
            description:
              'Description for the CLM for Professional Services Automation  card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.contractLifecycleManagementForProfessionalServicesAutomation',
            defaultMessage:
              'Contract Lifecycle Management for Professional Services Automation',
            description:
              'Contract Lifecycle Management for Professional Services Automation integrations card label',
          }),
          name: 'contract-lifecycle-management-for-professional-services-automation',
          provider: 'workday',
        },
      ],
    },
    ELECTRONIC_SIGNATURES: {
      isLoading: isLoadingEsignatureIntegration,
      label: intl.formatMessage({
        id: 'Integrations.electronicSignaturesSection',
        defaultMessage: 'Electronic Signatures',
        description: 'Electronic Signatures integrations section label',
      }),
      name: 'electronic signature',
      cards: [
        {
          chips: getChipStatusProps(
            ['e-signature', 'clm'],
            chipTranslationKeys,
          ),
          hoverAction: isEsignatureIntegrationActive(
            esignIntegrations,
            EsignatureType.Adobesign,
          )
            ? {
                ...baseManageAction,
                onClick: () => {
                  dispatch(
                    slices.newIntegrations.actions.setAction(
                      EsignatureType.Adobesign,
                    ),
                  );
                  dispatch(
                    slices.newIntegrations.actions.setEsignatureIntegration(
                      esignIntegrations.find(
                        (integration: any) =>
                          integration?.provider === EsignatureType.Adobesign,
                      ),
                    ),
                  );
                },
              }
            : undefined,
          actions: [
            {
              ...getActionStatus(
                EsignatureType.Adobesign,
                accessConditions,
                esignIntegrations,
              ),
              disabled: shouldDisableEsignatureConfigure(
                esignIntegrations,
                EsignatureType.Adobesign,
              ),
              tooltip: shouldDisableEsignatureConfigure(
                esignIntegrations,
                EsignatureType.Adobesign,
              )
                ? disableEsignatureConfigureTooltip
                : undefined,
              onClick: () => {
                dispatch(
                  slices.newIntegrations.actions.setAction(
                    EsignatureType.Adobesign,
                  ),
                );
                dispatch(
                  slices.newIntegrations.actions.setEsignatureIntegration(
                    esignIntegrations.find(
                      (integration: any) =>
                        integration?.provider === EsignatureType.Adobesign,
                    ),
                  ),
                );
              },
            },
          ],
          description: intl.formatMessage({
            id: 'Integrations.adobeSignDescription',
            defaultMessage:
              'An integration designed to enable users to send and receive documents for e-signatures directly within the platform, streamlining business processes.',
            description: 'Description for the Adobe Sign card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.adobeSignCard',
            defaultMessage: 'Adobe Sign',
            description: 'Adobe Sign integrations card label',
          }),
          name: 'adobe-sign',
          provider: EsignatureType.Adobesign,
        },
        {
          chips: getChipStatusProps(
            ['e-signature', 'clm'],
            chipTranslationKeys,
          ),
          description: intl.formatMessage({
            id: 'Integrations.docusignDescription',
            defaultMessage:
              'An integration designed to enable users to send and receive documents for e-signatures directly within the platform, streamlining business processes.',
            description: 'Description for the Docusign card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.docusignCard',
            defaultMessage: 'Docusign',
            description: 'Docusign integrations card label',
          }),
          name: 'docusign',
          provider: EsignatureType.Docusign,
          hoverAction: isEsignatureIntegrationActive(
            esignIntegrations,
            EsignatureType.Docusign,
          )
            ? {
                ...baseManageAction,
                onClick: () => {
                  dispatch(
                    slices.newIntegrations.actions.setAction(
                      EsignatureType.Docusign,
                    ),
                  );
                  dispatch(
                    slices.newIntegrations.actions.setEsignatureIntegration(
                      esignIntegrations.find(
                        (integration) =>
                          integration.provider === EsignatureType.Docusign,
                      ),
                    ),
                  );
                },
              }
            : undefined,
          actions: [
            {
              ...getActionStatus(
                EsignatureType.Docusign,
                accessConditions,
                esignIntegrations,
              ),
              disabled: shouldDisableEsignatureConfigure(
                esignIntegrations,
                EsignatureType.Docusign,
              ),
              tooltip: shouldDisableEsignatureConfigure(
                esignIntegrations,
                EsignatureType.Docusign,
              )
                ? disableEsignatureConfigureTooltip
                : undefined,
              onClick: () => {
                dispatch(
                  slices.newIntegrations.actions.setAction(
                    EsignatureType.Docusign,
                  ),
                );
                dispatch(
                  slices.newIntegrations.actions.setEsignatureIntegration(
                    esignIntegrations.find(
                      (integration) =>
                        integration.provider === EsignatureType.Docusign,
                    ),
                  ),
                );
              },
            },
          ],
        },
      ],
    },
    DOCUMENT_EDITING: {
      featureFlag: enableDocumentEditing,
      label: intl.formatMessage({
        id: 'Integrations.documentEditingSection',
        defaultMessage: 'Document Editing',
        description: 'Document Editing integrations section label',
      }),
      name: 'document editing',
      cards: [
        {
          chips: getChipStatusProps(['editor', 'clm'], chipTranslationKeys),
          actions: [
            {
              ...(hasConfiguredDocumentEditing ? configured : configure),
              onClick: () => {
                dispatch(
                  slices.newIntegrations.actions.setAction('document-editing'),
                );
              },
            },
          ],
          description: intl.formatMessage({
            id: 'Integrations.documentEditingSectionDescription',
            defaultMessage:
              'An upcoming integration that will allow users to work directly within Microsoft Word, eliminating tool switching and enhancing their experience.',
            description: 'Description for the Document Editing section',
          }),
          label: intl.formatMessage({
            id: 'Integrations.microsoftWordCard',
            defaultMessage: 'Microsoft Word',
            description: 'Microsoft Word integrations card label',
          }),
          name: 'microsoft-word',
          provider: 'word',
        },
      ],
    },
    DOCUMENT_MANAGEMENT: {
      isLoading: isLoadingToken || isLoadingStorageSyncPairs,
      enableManageIntegrations: true,
      label: intl.formatMessage({
        id: 'Integrations.documentManagementSection',
        defaultMessage: 'Document Management',
        description: 'Document Management integrations section label',
      }),
      name: 'document management',
      cards: [
        {
          chips: getChipStatusProps(
            ['storage provider', 'clm', 'ci'],
            chipTranslationKeys,
          ),
          description: intl.formatMessage({
            id: 'Integrations.boxDescription',
            defaultMessage:
              'This integration connects to the Box cloud storage service, allowing users to sync and manage documents between Box and Workday CLM/CI.',
            description: 'Description for the Box card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.boxCard',
            defaultMessage: 'Box',
            description: 'Box integrations card label',
          }),
          name: 'Box',
          provider: StorageProviderType.Box,
          hoverAction: storageManageAction,
          actions: [
            {
              ...getActionStatus(
                StorageProviderType.Box,
                accessConditions,
                syncPairs,
              ),
              onClick: () =>
                handleStorageProviderAuth('Box', StorageProviderType.Box),
            },
          ],
        },
        {
          chips: getChipStatusProps(
            ['storage provider', 'clm'],
            chipTranslationKeys,
          ),
          hoverAction: storageManageAction,
          actions: [
            {
              ...getActionStatus(
                StorageProviderType.Dropbox,
                accessConditions,
                syncPairs,
              ),
              onClick: () =>
                handleStorageProviderAuth(
                  'Dropbox',
                  StorageProviderType.Dropbox,
                ),
            },
          ],
          description: intl.formatMessage({
            id: 'Integrations.dropboxDescription',
            defaultMessage:
              "This integration provides connectivity with Dropbox's cloud storage, enabling seamless file synchronization and access.",
            description: 'Description for the Dropbox card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.dropboxCard',
            defaultMessage: 'Dropbox',
            description: 'Dropbox integrations card label',
          }),
          name: 'Dropbox',
          provider: StorageProviderType.Dropbox,
        },
        {
          chips: getChipStatusProps(
            ['storage provider', 'clm'],
            chipTranslationKeys,
          ),
          description: intl.formatMessage({
            id: 'Integrations.googleDriveDescription',
            defaultMessage:
              'An integration that links to Google Drive, allowing for the management and synchronization of documents stored there.',
            description: 'Description for the Google Drive card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.googleDriveCard',
            defaultMessage: 'Google Drive',
            description: 'Google Drive integrations card label',
          }),
          name: 'Google Drive',
          provider: StorageProviderType.GoogleDrive,
          hoverAction: storageManageAction,
          actions: [
            {
              ...getActionStatus(
                StorageProviderType.GoogleDrive,
                accessConditions,
                syncPairs,
              ),
              onClick: () =>
                handleStorageProviderAuth(
                  'Google Drive',
                  StorageProviderType.GoogleDrive,
                ),
            },
          ],
        },
        {
          chips: getChipStatusProps(
            ['storage provider', 'clm'],
            chipTranslationKeys,
          ),
          hoverAction: storageManageAction,
          actions: [
            {
              ...getActionStatus(
                StorageProviderType.OneDrive,
                accessConditions,
                syncPairs,
              ),
              onClick: () =>
                handleStorageProviderAuth(
                  'OneDrive',
                  StorageProviderType.OneDrive,
                ),
            },
          ],
          description: intl.formatMessage({
            id: 'Integrations.oneDriveDescription',
            defaultMessage:
              'This integration connects with Microsoft OneDrive, facilitating file access, storage, and synchronization.',
            description: 'Description for the OneDrive card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.oneDriveCard',
            defaultMessage: 'OneDrive',
            description: 'OneDrive integrations card label',
          }),
          name: 'OneDrive',
          provider: StorageProviderType.OneDrive,
        },
        {
          chips: getChipStatusProps(
            ['storage provider', 'clm'],
            chipTranslationKeys,
          ),
          description: intl.formatMessage({
            id: 'Integrations.sharePointDescription',
            defaultMessage:
              'An integration with SharePoint that enables the management of documents stored in the system.',
            description: 'Description for the SharePoint card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.sharePointCard',
            defaultMessage: 'SharePoint',
            description: 'SharePoint integrations card label',
          }),
          name: 'SharePoint',
          provider: StorageProviderType.SharePoint,
          hoverAction: storageManageAction,
          actions: [
            {
              ...getActionStatus(
                StorageProviderType.SharePoint,
                accessConditions,
                syncPairs,
              ),
              onClick: () =>
                handleStorageProviderAuth(
                  'SharePoint',
                  StorageProviderType.SharePoint,
                ),
            },
          ],
        },
      ],
    },
    CRM: {
      isLoading: isLoadingCrmConnections,
      label: intl.formatMessage({
        id: 'Integrations.crmSection',
        defaultMessage: 'CRM',
        description: 'CRM integrations section label',
      }),
      name: 'crm',
      cards: [
        {
          chips: getChipStatusProps(['crm', 'clm'], chipTranslationKeys),
          description: intl.formatMessage({
            id: 'Integrations.salesforceDescription',
            defaultMessage:
              'This integration connects with Salesforce; syncing contract data and metadata between the two platforms for enhanced CRM and sales processes.',
            description: 'Description for the Salesforce card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.salesforceCard',
            defaultMessage: 'Salesforce',
            description: 'Salesforce integrations card label',
          }),
          name: 'salesforce',
          provider: CrmType.Salesforce,
          hoverAction: {
            ...baseManageAction,
            onClick: () =>
              navigate(
                generatePath(RoutePathType.AdminConsoleClientCRM, {
                  clientId: clientId as string,
                }),
              ),
          },
          actions: [
            {
              ...getActionStatus(
                CrmType.Salesforce,
                accessConditions,
                syncPairs,
              ),
              disabled: !hasApiIntegration,
              tooltip: !hasApiIntegration
                ? intl.formatMessage({
                    id: 'Integrations.salesforceTooltip',
                    defaultMessage:
                      'This integration is not currently enabled for your account.',
                    description: 'Tooltip for the Salesforce integration',
                  })
                : undefined,

              onClick: () =>
                dispatch(
                  slices.newIntegrations.actions.setAction('salesforce'),
                ),
            },
          ],
        },
      ],
    },
    PROCUREMENT: {
      isLoading: isLoadingAribaSyncPairs,
      label: intl.formatMessage({
        id: 'Integrations.procurementSection',
        defaultMessage: 'Procurement',
        description: 'Procurement integrations section label',
      }),
      name: 'procurement',
      featureFlag: enableAribaIntegration,
      cards: [
        {
          chips: getChipStatusProps(
            ['procurement', 'clm'],
            chipTranslationKeys,
          ),
          description: intl.formatMessage({
            id: 'Integrations.aribaDescription',
            defaultMessage:
              'An integration with the SAP Ariba platform, used for managing procurement and contracts.',
            description: 'Description for the Ariba card',
          }),
          label: intl.formatMessage({
            id: 'Integrations.aribaCard',
            defaultMessage: 'Ariba',
            description: 'Ariba integrations card label',
          }),
          name: 'ariba',
          provider: ProcurementIntegrationType.Ariba,
          hoverAction: {
            ...baseManageAction,
            onClick: () =>
              navigate(
                generatePath(RoutePathType.AdminConsoleClientProcurement, {
                  clientId: clientId as string,
                }),
              ),
          },
          actions: [
            {
              ...getActionStatus(
                ProcurementIntegrationType.Ariba,
                accessConditions,
                syncPairs.filter(
                  (sp) =>
                    String(sp.provider) ===
                    String(ProcurementIntegrationType.Ariba),
                ),
              ),
              onClick: () => {
                const syncPairData = createSyncPairData({
                  name: 'Ariba',
                  provider: ProcurementIntegrationType.Ariba,
                });
                dispatch(
                  slices.integrations.actions.setActiveSyncPair(syncPairData),
                );
                dispatch(
                  slices.integrations.actions.setManageSyncPairStage(
                    ManageSyncPairStageType.MultiAPIs,
                  ),
                );
              },
            },
          ],
        },
      ],
    },
    DEVELOPER_TOOLS: {
      label: intl.formatMessage({
        id: 'Integrations.developerToolsSection',
        defaultMessage: 'Developer Tools',
        description: 'Developer Tools integrations section label',
      }),
      name: 'developer tools',
      cards: [
        {
          chips: getChipStatusProps(
            ['developer tools', 'clm'],
            chipTranslationKeys,
          ),
          description: intl.formatMessage({
            id: 'Integrations.apiDescription',
            defaultMessage:
              'This integration is an API that enables developers to build custom applications and workflows.',
            description: 'Description for the API card',
          }),
          name: 'platform-api',
          label: intl.formatMessage({
            id: 'Integrations.apiCard',
            defaultMessage: 'Platform API',
            description: 'Platform API integrations card label',
          }),
          provider: 'evisort',
          actions: [
            {
              ...configure,
              onClick: () =>
                navigate(
                  generatePath(RoutePathType.AdminConsoleClientApiManagement, {
                    clientId: clientId as string,
                  }),
                ),
            },
          ],
        },
        {
          chips: getChipStatusProps(
            ['developer tools', 'clm'],
            chipTranslationKeys,
          ),
          actions: [
            {
              ...(hasConfiguredEndpoints ? configured : configure),
              onClick: () =>
                navigate(
                  generatePath(RoutePathType.AdminConsoleClientWebhooks, {
                    clientId: clientId as string,
                  }),
                ),
            },
          ],
          description: intl.formatMessage({
            id: 'Integrations.webhooksDescription',
            defaultMessage:
              'This integration is an API that enables developers to build custom applications and workflows.',
            description: 'Description for the Webhooks card',
          }),
          name: 'webhooks',
          label: intl.formatMessage({
            id: 'Integrations.webhooksCard',
            defaultMessage: 'Webhooks',
            description: 'Webhooks integrations section label',
          }),
          featureFlag: enableWebhooksIntegration,
          provider: 'evisort',
        },
        {
          chips: getChipStatusProps(
            ['developer tools', 'clm'],
            chipTranslationKeys,
          ),
          description: intl.formatMessage({
            id: 'Integrations.emailIntakeDescription',
            defaultMessage:
              'Automate the processing of incoming emails, converting them into structured data to create or update records in another system.',
            description: 'Description for the Email Intake card',
          }),
          name: 'email-intake',
          label: intl.formatMessage({
            id: 'Integrations.emailIntakeCard',
            defaultMessage: 'Email Intake',
            description: 'Email Intake integrations section label',
          }),
          provider: 'evisort',
          actions: [
            {
              ...configure,
              onClick: () =>
                navigate(
                  generatePath(RoutePathType.AdminConsoleClientEmailIntake, {
                    clientId: clientId as string,
                  }),
                ),
            },
          ],
        },
      ],
    },
  };

  const enabledSections = Object.values(sections)
    .filter((integration) => checkFlagIncluded(integration))
    .map((section) => {
      const filteredCards = section.cards.filter(
        (card) =>
          checkFlagIncluded(card) &&
          cardMatchesFilters(filters)(card, section.name) &&
          cardMatchesSearch(card, section.label, section.name, search),
      );

      return {
        ...section,
        cards: filteredCards,
      };
    })
    .filter((section) => section.cards.length > 0);

  const placeholderContent =
    enabledSections.length === 0
      ? {
          icon: 'search' as const,
          title: intl.formatMessage({
            id: 'Integrations.noResultsMessage',
            defaultMessage: 'No integrations match your filters',
          }),
          description: intl.formatMessage({
            id: 'Integrations.noResultsDescription',
            defaultMessage: 'Try adjusting your search or filter criteria',
          }),
        }
      : undefined;

  return (
    <ContentContainer placeholderContent={placeholderContent}>
      <Layout preset="sections">
        {enabledSections.map((section) => {
          return (
            <Section key={section.name} title={section.label}>
              <CardsGrid
                isLoading={(section as any).isLoading}
                cards={section.cards}
                renderItem={(integration) => (
                  <IntegrationCard integration={integration} search={search} />
                )}
                gridOptions={{
                  columns: 'auto-fit',
                  columnMinWidth: 300,
                  columnMaxWidth: 450,
                  width: '100%',
                }}
              />
            </Section>
          );
        })}
      </Layout>
    </ContentContainer>
  );
};
